import AWS from "aws-sdk";
import {
    getIdentityPoolId,
    getIdentityPoolIdRegion,
    getOperationalConfig
} from "./network/BaseURLUtils";
import {ensureOperationalIdToken, getOperationalIdTokenSync} from "./OperationalAuth";

/**
 * Builds the Cognito `Logins` map that turns the operational identity pool call
 * into an *authenticated* GetCredentialsForIdentity request instead of a guest
 * (unauthenticated) one (Shepherd eaa16814).
 *
 * The identity pool is UNCHANGED — we keep calling getIdentityPoolId() (the
 * existing pool). The CDK secures that same pool in place: it attaches the new
 * operational user pool as a login provider and sets
 * AllowUnauthenticatedIdentities=false. So there is NO pool-ID cutover; the app
 * just needs to present a token from that provider.
 *
 * The token is minted against the operational user pool by OperationalAuth
 * (read dummy creds from S3 via a guest credentials identity pool -> initiateAuth
 * -> IdToken). This is NOT the katal-logger token.
 *
 * Returns `undefined` when the operational pool is not configured yet
 * (pre-deploy) or no token is available. In that window the existing pool still
 * allows guest, so getS3Client() keeps working unauthenticated; once the CDK
 * flips guest off, a missing token means the call is correctly rejected.
 */
const buildLogins = () => {
    const operationalConfig = getOperationalConfig();
    if (!operationalConfig) {
        return undefined; // not configured yet; no Logins (guest until CDK deploy)
    }
    const idToken = getOperationalIdTokenSync();
    if (!idToken) {
        return undefined;
    }
    const providerName =
        `cognito-idp.${operationalConfig.region}.amazonaws.com/${operationalConfig.userPoolId}`;
    return {
        [providerName]: idToken
    };
}

/**
 * Proactively mint/refresh the operational IdToken. Should be awaited by the
 * app's periodic sync loop (and on startup) so getS3Client() has a fresh token
 * to place in the Logins map. Safe no-op before cutover.
 */
const refreshS3Auth = async () => {
    return ensureOperationalIdToken();
}

const getS3Client = () => {
    const logins = buildLogins();

    const cognitoConfig = {
        // Unchanged: the existing operational identity pool (secured in place).
        IdentityPoolId: getIdentityPoolId()
    };

    // Only attach Logins when we have an operational token. Attaching an
    // empty/undefined Logins map would still request guest credentials, so we
    // keep the config free of it in that case rather than sending junk.
    if (logins) {
        cognitoConfig.Logins = logins;
    }

    AWS.config.update({
        region: getIdentityPoolIdRegion(),
        credentials: new AWS.CognitoIdentityCredentials(cognitoConfig),
        maxRetries: 3
    });

    return new AWS.S3();
}

export {getS3Client, refreshS3Auth}
